import { cn } from "@monbits/ui";
import * as React from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import type { PoolWithdrawal } from "src/providers/pool-withdrawal-provider";
import {
	usePendingPoolWithdrawals,
	usePoolWithdrawals,
} from "src/queries/pool-withdrawal-queries";

const TABS = [
	{ path: "/pool-withdrawals/pending", label: "Pending Approval" },
	{ path: "/pool-withdrawals/all", label: "All Withdrawals" },
] as const;

export const PoolWithdrawalsLayout: React.FC = () => {
	const location = useLocation();

	const pendingQuery = usePendingPoolWithdrawals();
	const allQuery = usePoolWithdrawals({ limit: 1, offset: 0 });

	const totalPending = pendingQuery.data?.pagination.totalCount ?? 0;
	const totalAll = allQuery.data?.pagination.totalCount ?? 0;

	const pendingWithdrawals = pendingQuery.data?.withdrawals ?? [];
	const allWithdrawals =
		usePoolWithdrawals({
			limit: 100,
			offset: 0,
		}).data?.withdrawals ?? [];

	const combinedWithdrawals = React.useMemo(() => {
		const byId = new Map<number, PoolWithdrawal>();
		for (const item of pendingWithdrawals) {
			byId.set(item.id, item);
		}
		for (const item of allWithdrawals) {
			if (!byId.has(item.id)) {
				byId.set(item.id, item);
			}
		}
		return Array.from(byId.values());
	}, [pendingWithdrawals, allWithdrawals]);

	const getLabel = (tab: (typeof TABS)[number]) => {
		if (tab.path === "/pool-withdrawals/pending") {
			return `${tab.label} (${totalPending})`;
		}
		return `${tab.label} (${totalAll})`;
	};

	return (
		<div className="p-4 sm:p-6">
			<h1 className="text-3xl font-bold mb-6">Pool Withdrawals</h1>

			<div className="flex items-center border-b mb-6">
				{TABS.map((tab) => {
					const isActive = location.pathname.startsWith(tab.path);

					return (
						<Link
							key={tab.path}
							to={tab.path + location.search}
							className={cn(
								"px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
								isActive
									? "border-primary text-foreground"
									: "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground",
							)}
						>
							{getLabel(tab)}
						</Link>
					);
				})}
			</div>

			<Outlet
				context={{
					withdrawals: combinedWithdrawals,
					isLoading: pendingQuery.isLoading || allQuery.isLoading,
				}}
			/>
		</div>
	);
};

export type PoolWithdrawalsOutletContext = {
	withdrawals: PoolWithdrawal[];
	isLoading: boolean;
};
